import { useEffect, useState, type FormEvent } from 'react'
import { Search, X } from 'lucide-react'
import { Input } from '@teach/ui'
import { useGetCategoriesQuery } from '@/features/categories/categoriesApi'
import { useLanguage } from '@/hooks/useLanguage'
import { useDebouncedValue } from '@/lib/useDebouncedValue'
import type { LangFilter } from '@/types/lang'
import { CategoryFilterDropdown } from './filters/CategoryFilterDropdown'
import { LanguageFilterDropdown } from './filters/LanguageFilterDropdown'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  search: string
  categoriesFilter: number[]
  languageFilter: LangFilter
  onSearchSubmit: (search: string) => void
  onCategoriesChange: (ids: number[]) => void
  onLanguageFilterChange: (next: LangFilter) => void
}

export const CatalogFilterBar = ({
  search,
  categoriesFilter,
  languageFilter,
  onSearchSubmit,
  onCategoriesChange,
  onLanguageFilterChange,
}: Props) => {
  const { language } = useLanguage()
  const m = useMessages(messages)
  const { data: categories } = useGetCategoriesQuery({ kind: 'course', languageFilter })
  const [localSearch, setLocalSearch] = useState(search)

  // Keep local input in sync when URL search changes (e.g. via chip removal)
  useEffect(() => setLocalSearch(search), [search])
  // Фильтр — сразу при вводе (после короткой паузы), без Enter.
  const debounced = useDebouncedValue(localSearch.trim())
  useEffect(() => {
    if (debounced !== search) onSearchSubmit(debounced)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- реагируем только на паузу в вводе
  }, [debounced])

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    onSearchSubmit(localSearch.trim())
  }

  const toggleCategory = (id: number) => {
    if (categoriesFilter.includes(id)) {
      onCategoriesChange(categoriesFilter.filter((x) => x !== id))
    } else {
      onCategoriesChange([...categoriesFilter, id])
    }
  }

  return (
    <div className="mb-4 md:mb-6 space-y-3">
      {/* Поиск как в «Моих курсах»: крупный, на всю ширину; язык — списком рядом. */}
      <div className="flex items-center gap-2">
        <form onSubmit={handleSubmit} className="relative flex-1">
          <Search className="absolute left-5 top-1/2 size-5 -translate-y-1/2 text-muted-foreground" />
          {/* Обычное текстовое поле, а не type="search": у того свой синий крестик
              браузера, не из дизайн-системы. Очистка — своей кнопкой. */}
          <Input
            type="text"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            placeholder={m.searchPlaceholder}
            className="h-14 rounded-2xl border-0 bg-foreground/5 pl-14 pr-12 text-base"
          />
          {localSearch ? (
            <button
              type="button"
              aria-label={m.clearSearch}
              onClick={() => {
                setLocalSearch('')
                onSearchSubmit('')
              }}
              className="absolute right-4 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          ) : null}
        </form>
        <CategoryFilterDropdown
          categories={categories}
          categoriesFilter={categoriesFilter}
          language={language}
          onToggle={toggleCategory}
          onClear={() => onCategoriesChange([])}
          className="h-14 rounded-2xl"
        />
        <LanguageFilterDropdown
          value={languageFilter}
          onChange={onLanguageFilterChange}
          className="h-14 rounded-2xl"
        />
      </div>
    </div>
  )
}
